/**
 * Import Matter Sidebar Trigger
 *
 * Button in the sidebar to import a single matter from the firm's PMS.
 * Only visible when a PMS integration is connected and enabled.
 * Supports compact (icon-only) mode for the detail page icon rail.
 *
 * A firm has at most one PMS integration, so the connected provider is read straight off the status
 * and drives both the branding and which import flow opens — one button, whichever PMS the firm uses.
 */

'use client';

import { useState } from 'react';
import Image from 'next/image';
import { Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { usePmsStatus } from '@/lib/services/integrations/pms';
import { useOptionalUserProfile } from '@/lib/providers/user-profile-provider';
import { normalizeRole } from '@/lib/utils/permissions';
import { ImportMatterModal } from './import-matter-modal';
import { getPmsImportProviderConfig } from './import-provider-config';

interface ImportMatterTriggerProps {
  compact?: boolean;
  className?: string;
}

/**
 * Why import is unavailable while the firm's PMS integration has expired.
 *
 * **Disabled, never hidden.** Removing the button would leave the user with no signal about what
 * changed or why — and the action genuinely cannot succeed, so offering it is worse still (it costs
 * the user their session, VALE-281). The disabled control plus this sentence is the middle ground.
 *
 * Split by role for the same reason the expired-integration banner is: re-authenticating is an
 * admin's action, so telling a paralegal to go and do it names a remedy they do not have. The wording
 * tracks that banner's two variants (`LF-2416` seeds) so a user meeting both reads one story — and it
 * is exported so the pairing is guarded by a test rather than asserted only through the DOM, matching
 * `PMS_CONNECT_BLOCKED_REASON` and `pmsDisconnectCopy` on the integrations tab.
 */
export function pmsImportExpiredReason(
  providerLabel: string,
  isAdmin: boolean
): string {
  return isAdmin
    ? `Your ${providerLabel} connection has expired. Re-authenticate in Account → Integrations to import matters.`
    : `The ${providerLabel} connection has expired. Please contact your administrator to re-authenticate.`;
}

export function ImportMatterTrigger({
  compact = false,
  className
}: ImportMatterTriggerProps) {
  const { data: pmsStatus } = usePmsStatus();
  const { profile } = useOptionalUserProfile();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const provider = pmsStatus?.provider;

  // Visible to every firm user once an admin has enabled the integration —
  // gated on connection status only, not the admin-only `pms-integrations`
  // feature flag (that flag gates the admin Integrations settings surface).
  if (!provider || !pmsStatus?.isEnabled) return null;

  const config = getPmsImportProviderConfig(provider);

  // Read the expiry off the backend's own `requiresReauth` rather than re-deriving it from token
  // fields — see `PmsStatus`. `isEnabled` stays true through an expiry, so it cannot answer this.
  const disabledReason = pmsStatus.requiresReauth
    ? pmsImportExpiredReason(
        config.label,
        normalizeRole(profile?.role) === 'admin'
      )
    : undefined;
  const isDisabled = !!disabledReason;

  // A provider with no brand asset gets a neutral icon rather than a fabricated logo. Both current
  // providers ship one; the fallback exists for whichever comes next.
  const providerIcon = config.logoUrl ? (
    <Image src={config.logoUrl} alt={config.label} width={16} height={16} />
  ) : (
    <Building2 className="h-4 w-4" aria-hidden />
  );

  const compactButton = (
    <Button
      variant="outline"
      size="icon"
      className={cn('h-8 w-8 rounded-lg', className)}
      disabled={isDisabled}
      onClick={() => setIsModalOpen(true)}
    >
      {providerIcon}
      <span className="sr-only">Import Matter</span>
    </Button>
  );

  const fullButton = (
    <Button
      variant="outline"
      size="sm"
      className={cn('bg-card h-8 w-full justify-start rounded-lg', className)}
      disabled={isDisabled}
      onClick={() => setIsModalOpen(true)}
    >
      <span className="mr-2">{providerIcon}</span>
      <span>Import Matter</span>
    </Button>
  );

  return (
    <>
      {compact ? (
        <Tooltip delayDuration={0}>
          {/* Wrapped: a disabled button emits no pointer events, so the tooltip would never open on
              the one state that needs explaining. */}
          <TooltipTrigger asChild>
            <span>{compactButton}</span>
          </TooltipTrigger>
          {/* `max-w-xs`: `TooltipContent` has no width cap, so a prose reason renders as one
              unwrapped line and runs off-screen — the short "Import Matter" label never showed it. */}
          <TooltipContent side="right" sideOffset={8} className="max-w-xs">
            {disabledReason ?? 'Import Matter'}
          </TooltipContent>
        </Tooltip>
      ) : isDisabled ? (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <span className="block w-full">{fullButton}</span>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8} className="max-w-xs">
            {disabledReason}
          </TooltipContent>
        </Tooltip>
      ) : (
        // The enabled full button carries its label in the button itself, so a tooltip would only
        // restate it — the compact form needs one because its label is icon-only.
        fullButton
      )}

      {/* Not rendered while expired: the trigger cannot open it, and a modal that can never open is
          dead weight in two of the app's most-mounted trees. */}
      {!isDisabled && (
        <ImportMatterModal
          provider={provider}
          open={isModalOpen}
          onOpenChange={setIsModalOpen}
        />
      )}
    </>
  );
}
